import React, { forwardRef } from 'react';
import { ReactComponent as TodueMarkSvg } from '@/assets/logo/todue-mark.svg';

// Todue wordmark — full logo: text "todue" + 4-color mark vpravo hore
// Text uses currentColor so it adapts to light/dark theme via CSS color.
// `partRefs` (optional) gives per-part refs for animating "to", "due", "mark" separately.
// Text and mark live in two separate <svg>s so the text can be safely clipped
// (overflow: hidden) while the mark rotates/scales freely (overflow: visible).
export const TodueWordmark = forwardRef(function TodueWordmark({ height = 40, color, partRefs }, ref) {
  const refs = partRefs || {};
  // Original combined viewBox was 0 0 430 120. Text occupies 0-388 horizontally
  // (last "e" path ends at x≈385). Mark occupies 379-426 x, 4-50 y.
  const TEXT_VB_W = 388;
  const TEXT_VB_H = 120;
  const MARK_VB_W = 47;
  const MARK_VB_H = 46;
  // Mark visually overlaps "e" by ~6 SVG units → preserve via negative marginLeft
  const markOverlapPx = (height * 6) / TEXT_VB_H;
  const markHeightPx = (height * MARK_VB_H) / TEXT_VB_H;
  const wrapperStyle = {
    display: 'inline-flex',
    alignItems: 'flex-start',
    height,
    lineHeight: 0,
    ...(color ? { color } : null),
  };
  return (
    <span ref={ref} style={wrapperStyle} role="img" aria-label="todue">
      <svg
        height={height}
        viewBox={`0 0 ${TEXT_VB_W} ${TEXT_VB_H}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', overflow: 'hidden' }}
        aria-hidden="true"
      >
        <defs>
          <style>{`.st1{fill:currentColor}`}</style>
        </defs>
        <g ref={refs.to}>
          <path className="st1" d="M4.45,48.12v-10.22h17.13V8.74h11.27v29.16h23.15v10.22h-23.15v39.98c0,11.12,4.36,15.63,15.48,15.63h7.67v10.22h-7.67c-18.34,0-26.75-8.42-26.75-26.3v-39.53H4.45Z"/>
          <path className="st1" d="M93.6,36.39c22.7,0,39.98,17.13,39.98,39.53s-17.29,39.53-39.98,39.53-39.98-16.98-39.98-39.53,17.28-39.53,39.98-39.53ZM93.6,104.93c16.08,0,28.41-12.48,28.41-29.01s-12.33-29.01-28.41-29.01-28.41,12.48-28.41,29.01,12.33,29.01,28.41,29.01Z"/>
        </g>
        <g ref={refs.due}>
          <path className="st1" d="M197.98,4.55h23.65v109.4h-23.65v-7.39c-5.62,5.62-13.01,8.87-22.03,8.87-20.55,0-36.22-17.3-36.22-39.92s15.67-39.92,36.22-39.92c9.02,0,16.41,3.25,22.03,8.87V4.55ZM197.98,75.51c0-10.5-7.24-18.33-16.85-18.33s-16.85,7.84-16.85,18.33,7.24,18.33,16.85,18.33,16.85-7.84,16.85-18.33Z"/>
          <path className="st1" d="M227.84,37.07h23.65v44.06c0,7.69,5.32,12.71,13.31,12.71s13.31-5.03,13.31-12.71v-44.06h23.65v44.06c0,20.4-15.52,34.3-36.96,34.3s-36.96-13.9-36.96-34.3v-44.06Z"/>
          <path className="st1" d="M385.61,84.09h-55.74c2.81,6.8,9.17,11.24,17.44,11.24,6.8,0,12.71-3.1,16.11-8.57l19.22,8.87c-6.95,11.53-19.52,19.81-36.07,19.81-23.51,0-41.39-17.3-41.39-39.92s17.59-39.92,40.66-39.92c26.61,0,44.8,22.47,39.77,48.49ZM330.17,66.35h31.49c-2.66-6.51-8.28-10.64-15.82-10.64-7.1,0-12.86,4.29-15.67,10.64Z"/>
        </g>
      </svg>
      <svg
        height={markHeightPx}
        viewBox={`0 0 ${MARK_VB_W} ${MARK_VB_H}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', overflow: 'visible', marginLeft: -markOverlapPx }}
        aria-hidden="true"
      >
        <defs>
          <style>{`.st0{fill:#f24c4c}.st2{fill:#37b758}.st3{fill:#f2b01c}.st4{fill:#4ca7f2}`}</style>
        </defs>
        {/* Static parent translates original mark coords (379..426, 4..50) into mark-local 0..47, 0..46 viewBox.
            Theatre's transform is applied to the inner ref<g>, so it doesn't override this offset. */}
        <g transform="translate(-379 -4)">
          <g ref={refs.mark}>
            <path className="st4" d="M391.09,16.05h11.46V4.55h-14.38c-4.76,0-8.62,3.86-8.62,8.62v14.38h11.54v-11.5Z"/>
            <path className="st3" d="M402.55,4.55v11.5h11.54v11.5h11.46v-14.38c0-4.76-3.86-8.62-8.62-8.62h-14.38Z"/>
            <path className="st2" d="M391.09,39.05v-11.5h-11.54v14.38c0,4.76,3.86,8.62,8.62,8.62h14.38v-11.5h-11.46Z"/>
            <path className="st0" d="M414.09,39.05h-11.54v11.5h14.38c4.76,0,8.62-3.86,8.62-8.62v-14.38h-11.46v11.5Z"/>
          </g>
        </g>
      </svg>
    </span>
  );
});

// Todue mark — len 4-farebný symbol bez textu (favicon, app icon, kompaktný brand)
// Centrum je transparentné — preberie farbu pozadia.
export function TodueMark({ size = 32 }) {
  return (
    <TodueMarkSvg width={size} height={size} role="img" aria-label="todue" />
  );
}

// Stack of cards — legacy brand mark (used in Sidebar / Login / Onboarding / etc.)
// Kept until full rebrand sweep. Final colors: #ffc244 (bottom), #2bbf85 (middle), #3a7bd5 (top)
export function BrandMark({ size = 32, colors }) {
  const trio = Array.isArray(colors) && colors.length >= 3 ? colors : ['#ffc244', '#2bbf85', '#3a7bd5'];
  const [c1, c2, c3] = trio;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <rect x="2.4" y="10.4" width="20.8" height="14.4" rx="2.8" fill={c1} />
      <rect x="5.6" y="8" width="20.8" height="14.4" rx="2.8" fill={c2} />
      <rect x="8.8" y="5.6" width="20.8" height="14.4" rx="2.8" fill={c3} />
      <path d="M12.8 10.4h11.2M12.8 12.8h7.2" stroke="#fff" strokeWidth="0.96" strokeLinecap="round" />
    </svg>
  );
}

// Larger version for splash / login screen
export function BrandMarkLarge({ size = 80 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      <rect x="6" y="26" width="52" height="36" rx="7" fill="#ffc244" />
      <rect x="14" y="20" width="52" height="36" rx="7" fill="#2bbf85" />
      <rect x="22" y="14" width="52" height="36" rx="7" fill="#3a7bd5" />
      <path d="M32 26h28M32 32h18" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

export default BrandMark;
